import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { MetaData } from 'Components/MetaData';
import { TextImage } from 'Components/TextImage';

import { productsCategories, productsSlugs, productsSubCategories } from 'constants/products';

import { Faq } from 'Blocks/Faq';
import { HowItWorks } from 'Blocks/HowItWorks';
import { NoListBlock } from 'Blocks/NoListBlock';
import { Numbers } from 'Blocks/Numbers';
import { ProductsCarousel } from 'Blocks/ProductsCarousel';
import { ProductPageHero } from '../Blocks/ProductPageHero';

import IngredientsCTA from 'Containers/IngredientsCTA';
import ScrollTracker from 'Containers/ScrollTracker';

import { srcOrNextImageProp } from 'PropTypes/imageProps';
import productProps from 'PropTypes/productProps';

import { LegacyBaseScene as BaseScene } from 'Scenes/LegacyBaseScene';
import LoadingScene from 'Scenes/LoadingScene';

import { supplementCoreContent } from 'assets/content/productPages/supplement_core';
import productsStucturedData from 'assets/metadata/productsStructuredData';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import {
  getFilteredFetchedHaircareProductsIsLoaded,
  getSupplementsProductPageContent,
} from 'dux/products/selectors';
import { useFetchProductsList, useTrackProductViewed } from 'dux/products/hooks';

export const ProductDetailsSupplements = ({ content, filteredFetchedProductsIsLoaded }) => {
  const { isLoaded } = useFetchProductsList();
  const { isDesktop } = useResponsiveVariant();

  const { noListModule, heroModule, numbersModule, sustainabilityModule } = content;

  useTrackProductViewed({
    category: productsCategories.HAIRCARE,
    subcategory: productsSubCategories.SUPPLEMENTS,
    product: heroModule.product,
  });

  if (!isLoaded) {
    return <LoadingScene />;
  }

  return (
    <>
      <MetaData
        description="Stimulate hair growth with Prose Custom Root Source® Hair Supplements made with natural, clean ingredients. Shop today for longer, stronger hair in 90 days."
        structuredData={productsStucturedData[productsSlugs.SUPPLEMENT_CORE]}
        title="Custom Hair Supplements, Root Source® – Prose | Haircare"
      />

      <BaseScene footer FooterProps={{ hideCTA: true }} header>
        <ScrollTracker>
          <ProductPageHero content={content.heroModule} useShortLabel />
          <Numbers
            backgroundColor="darkerBeige"
            description={numbersModule.description}
            DescriptionProps={{ color: 'vert', marginBottom: 40, variant: 'p2' }}
            disclaimer={numbersModule.disclaimer}
            DisclaimerProps={{ color: 'vert' }}
            hasBorder
            LabelProps={{ color: 'vert', variant: 'p1' }}
            NumberProps={{ color: 'vert', variant: 'h2' }}
            numbers={numbersModule.numbers}
            title={numbersModule.title}
            TitleProps={{ color: 'vert', variantDesktop: 'h2', variantMobile: 'h3' }}
            variant="3up"
          />
          <NoListBlock content={noListModule} />
          {sustainabilityModule && (
            <TextImage
              description={sustainabilityModule.description}
              descriptionProps={{ color: 'noir', variant: 'p2' }}
              imageAlt="Two Supplements jars within an opened box next to two refill pouches, supplements pills and a glass of water half empty"
              imageDesktop={sustainabilityModule.imageDesktop}
              imageMobile={sustainabilityModule.imageMobile}
              imageMode={isDesktop ? 'contain-desktop' : undefined}
              title={sustainabilityModule.title}
              titleProps={{ variant: isDesktop ? 'h2' : 'h3', markupName: 'h2' }}
              wideScreenOrder="21"
            >
              <IngredientsCTA
                category={productsCategories.HAIRCARE}
                dataFrom="sustainability-module"
                variant="underlined"
              />
            </TextImage>
          )}
          <HowItWorks cta dataFrom="how-it-works-module" isHaircareOnly />
          <Faq
            backgroundColor={content.faqModule.backgroundColor}
            markupName="h3"
            questions={content.faqModule.questions}
            shouldGenerateJsonld
            title={content.faqModule.header}
            variant="noir"
          />
          {filteredFetchedProductsIsLoaded && (
            <ProductsCarousel background="cream" content={content.productsModule} />
          )}
        </ScrollTracker>
      </BaseScene>
    </>
  );
};

ProductDetailsSupplements.propTypes = {
  content: PropTypes.shape({
    noListModule: PropTypes.shape({
      cta: PropTypes.shape({
        label: PropTypes.string,
        link: PropTypes.string,
      }).isRequired,
      description: PropTypes.string.isRequired,
      image: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        imageDesktop: srcOrNextImageProp.isRequired,
        imageMobile: srcOrNextImageProp.isRequired,
      }),
      list: PropTypes.arrayOf(PropTypes.shape({})),
      title: PropTypes.string.isRequired,
    }),
    faqModule: PropTypes.shape({
      header: PropTypes.string.isRequired,
      backgroundColor: PropTypes.string,
      questions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    }).isRequired,
    heroModule: PropTypes.shape({
      product: productProps.isRequired,
    }).isRequired,
    numbersModule: PropTypes.shape({
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      disclaimer: PropTypes.oneOfType([PropTypes.string.isRequired, PropTypes.node.isRequired]),
      backgroundColor: PropTypes.string,
      numbers: PropTypes.arrayOf(PropTypes.shape({})),
      variant: PropTypes.string,
    }),
    productsModule: PropTypes.shape({
      backgroundColor: PropTypes.string,
    }),
    sampleFormulasSlider: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    sustainabilityModule: PropTypes.shape({
      description: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      imageAlt: PropTypes.string.isRequired,
      imageMobile: srcOrNextImageProp.isRequired,
      imageDesktop: srcOrNextImageProp.isRequired,
      imageTablet: srcOrNextImageProp,
    }),
  }).isRequired,
  filteredFetchedProductsIsLoaded: PropTypes.bool.isRequired,
};

export default connect(state => ({
  content: getSupplementsProductPageContent(state, { productDetails: supplementCoreContent }),
  filteredFetchedProductsIsLoaded: getFilteredFetchedHaircareProductsIsLoaded(state),
}))(ProductDetailsSupplements);
